import type { Audit, Permit, User } from 'src/types'
import type { RouteNamedMap } from 'vue-router/auto-routes'
import { mdiClose, mdiEye, mdiPencil, mdiPlus } from '@quasar/extras/mdi-v7'
import { colors, Dark } from 'quasar'

export const dates = ['bollo', 'assicurazione', 'noleggio', 'revisione'] as const

/** come TripRequest::MAX_ATTACHMENTS_BYTES: un file più grande non potrebbe mai essere allegato alla mail di un viaggio */
export const TRIP_MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024

export const tooltipProps = {
  class: 'bg-grey-9 text-caption',
  transitionHide: 'scale',
  transitionShow: 'scale',
}

export const chips = [
  // { label: 'APERTA', key: 0, color: 'grey' },
  { label: 'LAVORAZIONE', key: 50, color: 'orange' },
  { label: 'FINITA', key: 70, color: 'blue' },
  { label: 'CHIUSA', key: 110, color: 'green' },
] as const

export const toOptions = (arr: (string | number)[]) => arr.map(value => ({ value, label: `${value}` }))

export enum userTypesEnum {
  Amministratore = 'Amministratore',
  Carrozziere = 'Carrozziere',
  Levabolli = 'Levabolli',
}

export function userColor(user: User): string
export function userColor(user_id: number): string
export function userColor(userOrId: User | number): string {
  const user: User
    = typeof userOrId === 'number'
      ? (useApiStore().getObj(userOrId, 'users') as User)
      : userOrId

  if (user.type === userTypesEnum.Amministratore) return 'blue-grey'
  if (user.piva) return 'red'
  if (user.automotive) return 'orange'
  return user.type === userTypesEnum.Levabolli ? 'green' : 'blue'
}

export const permitColors: Record<Permit['type'], string> = {
  'Ferie': 'blue',
  'Permesso': 'green',
  'Rimborso': 'red',
  'Malattia': 'orange',
  'Assenza Ingiustificata': 'amber',
  'L.104': 'purple',
  'Congedo Parentale': 'teal',
  'Discesa': 'brown',
}

export const permitIcons: Record<Permit['type'], string> = {
  'Ferie': 'mdi-beach',
  'Permesso': 'mdi-clock-outline',
  'Rimborso': 'mdi-cash-refund',
  'Malattia': 'mdi-thermometer',
  'Assenza Ingiustificata': 'mdi-account-cancel-outline',
  'L.104': 'mdi-human-wheelchair',
  'Congedo Parentale': 'mdi-baby-carriage',
  'Discesa': 'mdi-home-export-outline',
}

export enum permitTypesEnum {
  Ferie = 'Ferie',
  Permesso = 'Permesso',
  Rimborso = 'Rimborso',
  Malattia = 'Malattia',
  Assenza = 'Assenza Ingiustificata',
  L104 = 'L.104',
  CongedoParentale = 'Congedo Parentale',
  Discesa = 'Discesa',
}

/** Richieste su più giorni (data inizio/fine), L.104 solo se non a ore */
export function isDailyPermit(permit: Pick<Permit, 'type' | 'start'>) {
  return [permitTypesEnum.Ferie, permitTypesEnum.Malattia, permitTypesEnum.Assenza, permitTypesEnum.CongedoParentale, permitTypesEnum.Discesa].includes(permit.type)
    || (permit.type === permitTypesEnum.L104 && !permit.start)
}

/** Richieste a ore (orario inizio/fine) */
export function isHourlyPermit(permit: Pick<Permit, 'type' | 'start'>) {
  return permit.type === permitTypesEnum.Permesso || (permit.type === permitTypesEnum.L104 && Boolean(permit.start))
}

export enum documentTypesEnum {
  ContrattoD = 'Contratto Determinato',
  ContrattoI = 'Contratto Indeterminato',
  Proroga = 'Proroga',
  CU = 'Certificazione Unica',
  BustePaga = 'Buste Paga',
}

/** Colore della palette Quasar per le pillole dei filtri: più chiaro in dark mode per mantenere il contrasto */
export function pillAccent(color: string) {
  return colors.getPaletteColor(color === 'primary' ? color : `${color}-${Dark.isActive ? 4 : 8}`)
}

/** Colore (palette Quasar) e icona per ogni tipo documento */
export const documentTypesMeta: Record<documentTypesEnum, { color: string, icon: string }> = {
  [documentTypesEnum.BustePaga]: { color: 'green', icon: 'mdi-cash-multiple' },
  [documentTypesEnum.ContrattoI]: { color: 'blue', icon: 'mdi-file-sign' },
  [documentTypesEnum.ContrattoD]: { color: 'orange', icon: 'mdi-file-clock-outline' },
  [documentTypesEnum.Proroga]: { color: 'red', icon: 'mdi-calendar-arrow-right' },
  [documentTypesEnum.CU]: { color: 'yellow', icon: 'mdi-file-certificate-outline' },
}

export const strongPasswordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/
export const validEmail = /^[\w.%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i

export const pauseOptions = [
  { label: '0 (no pausa)', value: 0 },
  { label: '30 min', value: 0.5 },
  { label: '1 h', value: 1 },
  { label: '1 h 30 min', value: 1.5 },
  { label: '2 h', value: 2 },
]

export const auditOperations = [
  { color: 'blue', icon: mdiEye },
  { color: 'green', icon: mdiPlus },
  { color: 'amber', icon: mdiPencil },
  { color: 'red', icon: mdiClose },
  // { color: 'brown', icon: mdiExport },
] as const

export const auditOperationsMap = {
  VIEW: auditOperations[0],
  STORE: auditOperations[1],
  EDIT: auditOperations[2],
  DELETE: auditOperations[3],
} as const

export const docRoutes: {
  names: Partial<Record<Audit['doc_type'], keyof RouteNamedMap>>
  icons: Partial<Record<Audit['doc_type'], string>>
} = {
  names: {
    'App\\Models\\User': '/users/[id]',
    'App\\Models\\Presence': '/presences/[id]',
    'App\\Models\\Client': '/clients/[id]',
  },
  icons: {
    'App\\Models\\Brand': 'mdi-car-convertible',
    'App\\Models\\BrandModel': 'mdi-car-convertible',
    'App\\Models\\Car': 'mdi-car',
    'App\\Models\\Client': 'mdi-ruler-square-compass',
    'App\\Models\\Document': 'mdi-file-document',
    'App\\Models\\Owner': 'mdi-account-star',
    'App\\Models\\Presence': 'mdi-pen',
    'App\\Models\\PresenceAttachment': 'mdi-paperclip',
    'App\\Models\\Paycheck': 'mdi-cash',
    'App\\Models\\Permit': 'mdi-calendar-check',
    'App\\Models\\MorphAttachment': 'mdi-paperclip',
    'App\\Models\\Tag': 'mdi-tag',
    'App\\Models\\User': 'mdi-account-group',
  },
}

export const icons: Readonly<Record<string, string>> = {
  'application/pdf': 'mdi-file-pdf-box',
  'image/png': 'mdi-file-image',
  'image/jpg': 'mdi-file-image',
  'image/jpeg': 'mdi-file-image',
  'application/octet-stream': 'mdi-file-excel',
}

export const iconColors: Readonly<Record<keyof typeof icons, string>> = {
  'application/pdf': 'red',
  'image/png': 'blue',
  'image/jpg': 'blue',
  'image/jpeg': 'blue',
  'application/octet-stream': 'green',
}

export const MONTHS = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
]
